"use client";

import React from "react";
import dynamic from "next/dynamic";

// Dynamically import to ensure client-only rendering
const DynamicFlowWrapper = dynamic(() => import("./index"), { ssr: false });

type ShortCodeDynamicFlowWrapperProps = {
  placement?: string;
  forced_flow_id?: string;
  jump_to_step_id?: string;
  degree?: string;
  category?: string;
  subject?: string;
  is_full_page_exp?: string;
  style_variant?: string;
  entry_step_id?: string;
  from_step_index?: string;
  debug_result?: string;
};

/**
 * Shortcode wrapper for DynamicFlowWrapper
 * Handles conversion from snake_case WordPress shortcode props to camelCase React props
 * Also handles conversion of string props to appropriate types (boolean, number, object)
 *
 * Usage in WordPress:
 * [dynamic-experience placement="dashboard" forced_flow_id="my-flow-id" /]
 * [dynamic-experience placement="aboveTheFold" is_full_page_exp="true" style_variant="alt" /]
 * [dynamic-experience degree="computer-science" category="tech" subject="programming" /]
 */
export function ShortCodeDynamicFlowWrapper({
  placement,
  forced_flow_id,
  jump_to_step_id,
  degree,
  category,
  subject,
  is_full_page_exp,
  style_variant,
  entry_step_id,
  from_step_index,
  debug_result,
}: ShortCodeDynamicFlowWrapperProps) {
  // Build dcs object only if any dcs-related props are provided
  const dcs =
    degree || category || subject
      ? {
          degree: degree || "",
          category: category || "",
          subject: subject || "",
        }
      : undefined;

  // Convert string boolean props to actual booleans
  const isFullPageExp = is_full_page_exp === "true";
  const debugResult = debug_result === "true";

  // Convert string number props to actual numbers
  const fromStepIndex = from_step_index
    ? parseInt(from_step_index, 10)
    : undefined;

  return (
    <DynamicFlowWrapper
      placement={placement as any}
      forcedFlowId={forced_flow_id}
      jumpToStepId={jump_to_step_id}
      dcs={dcs}
      isFullPageExp={isFullPageExp}
      styleVariant={style_variant as "default" | "alt" | undefined}
      entryStepId={entry_step_id}
      fromStepIndex={fromStepIndex}
      debugResult={debugResult}
    />
  );
}
